// @ts-nocheck -- staged migration: add explicit types before removing this directive.
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import Header from "../header/HeaderComponent";
import Footer from "../footer/FooterComponent";
import { blogPosts, getBlogPostBySlug } from "../../consts/blogPosts";
import { kanjiTextbookOrder } from "../../consts/kanjiTextbookOrder";
import { estimateReadingMinutes } from "../../utils/blogReadingTime";
import Seo from "../Seo/SeoComponent";
import BlogSubscribePrompt from "../NewsletterForm/BlogSubscribePrompt";
import styles from "./blogPostPage.module.scss";

function formatDate(dateString, locale = "en-US") {
    return new Date(`${dateString}T00:00:00`).toLocaleDateString(locale, {
        year: "numeric",
        month: "long",
        day: "numeric",
    });
}

const jlptLevels = [5, 4, 3, 2, 1];

// Surfaces kanji covered by a common teaching order (see kanjiTextbookOrder)
// first, in that order, ahead of everything else in the jōyō list.
const textbookPriority = new Map(kanjiTextbookOrder.map((symbol, index) => [symbol, index]));

const identity = (path) => path;
const englishMeaning = (symbol, meaning) => meaning;

function KanjiMeaningGrid({ items, localize = identity, meaningFor = englishMeaning }) {
    const { t } = useTranslation("blogPage");
    const [activeLevel, setActiveLevel] = useState(5);
    const filteredByLevel = activeLevel === "all" ? items : items.filter((item) => item.jlptLevel === activeLevel);
    const filtered = [...filteredByLevel].sort((a, b) => {
        const priorityA = textbookPriority.has(a.symbol) ? textbookPriority.get(a.symbol) : Infinity;
        const priorityB = textbookPriority.has(b.symbol) ? textbookPriority.get(b.symbol) : Infinity;
        return priorityA - priorityB;
    });

    return (
        <div>
            <div className={styles.jlptFilterRow}>
                <button
                    type="button"
                    className={`${styles.jlptFilterButton} ${activeLevel === "all" ? styles.jlptFilterButtonActive : ""}`}
                    onClick={() => setActiveLevel("all")}
                >
                    {t("post.all", { count: items.length })}
                </button>
                {jlptLevels.map((level) => (
                    <button
                        key={level}
                        type="button"
                        className={`${styles.jlptFilterButton} ${activeLevel === level ? styles.jlptFilterButtonActive : ""}`}
                        onClick={() => setActiveLevel(level)}
                    >
                        N{level} ({items.filter((item) => item.jlptLevel === level).length})
                    </button>
                ))}
            </div>
            <div className={styles.kanjiMeaningGrid}>
                {filtered.map((item) => (
                    <Link
                        key={item.symbol}
                        to={localize(`/kanji/${encodeURIComponent(item.symbol)}`)}
                        className={styles.kanjiMeaningTile}
                    >
                        <span className={styles.kanjiMeaningGlyph}>{item.symbol}</span>
                        <span className={styles.kanjiMeaningText}>{meaningFor(item.symbol, item.meaning)}</span>
                    </Link>
                ))}
            </div>
        </div>
    );
}

function ContentBlock({ block, index, localize = identity, meaningFor = englishMeaning }) {
    if (block.heading) {
        return (
            <h2 key={index} className={styles.sectionHeading}>
                {block.heading}
            </h2>
        );
    }

    if (block.kanjiMeaningGrid) {
        return <KanjiMeaningGrid key={index} items={block.kanjiMeaningGrid} localize={localize} meaningFor={meaningFor} />;
    }

    if (block.list) {
        return (
            <ul key={index} className={styles.list}>
                {block.list.map((item) => (
                    <li key={item}>{item}</li>
                ))}
            </ul>
        );
    }

    if (block.parts) {
        return (
            <p key={index} className={styles.paragraph}>
                {block.parts.map((part, partIndex) => {
                    if (typeof part === "string") {
                        return <React.Fragment key={partIndex}>{part}</React.Fragment>;
                    }
                    if (part.href) {
                        return (
                            <a
                                key={partIndex}
                                href={part.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.inlineLink}
                            >
                                {part.label}
                            </a>
                        );
                    }
                    return (
                        <Link key={partIndex} to={localize(part.to)} className={styles.inlineLink}>
                            {part.label}
                        </Link>
                    );
                })}
            </p>
        );
    }

    return (
        <p key={index} className={styles.paragraph}>
            {block.text}
        </p>
    );
}

export function blogPostAlternates(slug) {
    const post = getBlogPostBySlug(slug);
    if (!post) return [];
    const englishTwin = blogPosts.find((candidate) => candidate.ptSlug === slug);
    if (post.lang === "pt-PT") {
        return englishTwin
            ? [
                  { hrefLang: "en", path: `/blog/${englishTwin.slug}` },
                  { hrefLang: "pt-PT", path: `/blog/${slug}` },
              ]
            : [];
    }
    return [
        { hrefLang: "en", path: `/blog/${slug}` },
        { hrefLang: "pt-PT", path: post.ptSlug ? `/blog/${post.ptSlug}` : `/pt/blog/${slug}` },
    ];
}

export function BlogPostNotFound({ path, blogPath, localeLang }) {
    const { t } = useTranslation("blogPage");
    return (
        <div className={styles.page}>
            <Seo title={t("post.notFoundTitle")} path={path} noindex lang={localeLang} />
            <Header />

            <main className={styles.main}>
                <section className={styles.container}>
                    <h1 className={styles.title}>{t("post.notFoundTitle")}</h1>
                    <p className={styles.description}>{t("post.notFoundDescription")}</p>
                    <Link to={blogPath} className={styles.backLink}>
                        <FontAwesomeIcon icon={faArrowLeft} /> {t("post.backToBlog")}
                    </Link>
                </section>
            </main>

            <Footer />
        </div>
    );
}

export function BlogPostView({ post, path, blogPath = "/blog", localize = identity, meaningFor = englishMeaning, alternates = [] }) {
    const { t } = useTranslation("blogPage");
    const readingMinutes = estimateReadingMinutes(post.content);
    return (
        <div className={styles.page}>
            <Seo
                title={post.title}
                description={post.excerpt}
                path={path}
                lang={post.lang}
                alternates={alternates}
                keywords={post.keywords}
            />
            <Header />

            <main className={styles.main}>
                <article className={styles.container}>
                    <Link to={blogPath} className={styles.backLink}>
                        <FontAwesomeIcon icon={faArrowLeft} /> {t("post.backToBlog")}
                    </Link>

                    <header className={styles.postHeader}>
                        <div className={styles.postMeta}>
                            <time dateTime={post.date}>{formatDate(post.date, post.lang)}</time>
                            <span className={styles.postReadingTime}>
                                {t("post.readingTime", { count: readingMinutes })}
                            </span>
                            {post.tags?.map((tag) => (
                                <Link
                                    key={tag}
                                    to={`${blogPath}?tag=${encodeURIComponent(tag)}`}
                                    className={styles.postTag}
                                >
                                    {t(`tags.${tag}`, { defaultValue: tag })}
                                </Link>
                            ))}
                        </div>
                        <h1 className={styles.title}>{post.title}</h1>
                    </header>

                    <div className={styles.body}>
                        {post.content.map((block, index) => (
                            <ContentBlock key={index} block={block} index={index} localize={localize} meaningFor={meaningFor} />
                        ))}
                    </div>

                    <BlogSubscribePrompt />
                </article>
            </main>

            <Footer />
        </div>
    );
}

function BlogPostPage() {
    const { slug } = useParams();
    const post = getBlogPostBySlug(slug);

    if (!post) {
        return <BlogPostNotFound path={`/blog/${slug}`} blogPath="/blog" />;
    }

    return <BlogPostView post={post} path={`/blog/${post.slug}`} alternates={blogPostAlternates(post.slug)} />;
}

export default BlogPostPage;
